import type { WorldLivenessRollups } from "./worldLiveness";
import type { WorldUnseenSettleRollups } from "./worldUnseenSettle";
import { worldDisplayName, type NeucleosWorldSummary } from "./worldSelection";
import { ROOT_SESSION_STATUS_LABELS, type RootSessionStripThreadInput } from "../rootSessionStrip";
import { AGENT_STRIP_UNTITLED } from "../agentStrip";

export interface CrmReminder {
  reminderId: string;
  notificationRevision?: string;
  notificationReadState?: "read" | "unread" | "dismissed";
  activityId: string;
  state: "scheduled" | "delivered" | "cancelled" | "suppressed";
  remindAt: number | null;
  deliveredAt: number | null;
  reason: string | null;
  body: string;
  targetName: string;
  contactId: string | null;
  companyId: string | null;
  completedAt: number | null;
}

/** The session a World-activity notice is about: named in its label, opened by its click. */
export interface WorldAttentionSubject {
  title: string;
  environmentId: string;
  threadId: string;
  sessionId: string | null;
}

export interface WorldAttentionNotice {
  key: string;
  worldKey: string | null;
  place: string;
  label: string;
  kind: "attention" | "finished";
  subject?: WorldAttentionSubject;
}

const finishedMs = (thread: RootSessionStripThreadInput): number => {
  const ms = thread.finishedAt ? Date.parse(thread.finishedAt) : Number.NaN;
  return Number.isNaN(ms) ? 0 : ms;
};

/** World summaries are navigation hints, never a count of approval requests. */
export function worldAttentionNotices(
  worlds: ReadonlyArray<NeucleosWorldSummary>,
  activeWorldKey: string | null,
  localSelected: boolean,
  liveness: WorldLivenessRollups,
  unseen: WorldUnseenSettleRollups,
  threads: ReadonlyArray<RootSessionStripThreadInput> = [],
): WorldAttentionNotice[] {
  const notices: WorldAttentionNotice[] = [];
  const places = [{ worldKey: null, name: "Local" }, ...worlds];
  for (const status of ["failed", "approval", "input"] as const) {
    for (const world of places) {
      const standing =
        world.worldKey === null ? liveness.local : liveness.byWorldKey.get(world.worldKey);
      if (standing !== status) continue;
      // W-a67b0503: the notice names the session it is about (the newest one in
      // that state, the rollup's own membership rule) and opens it, never only
      // "Failed · <World>".
      const [first, ...rest] = threads
        .filter(
          (thread) =>
            (thread.worldKey ?? null) === world.worldKey &&
            thread.status === status &&
            thread.archivedAt === null &&
            thread.snoozedUntil === null &&
            thread.settledAt === null,
        )
        .sort((left, right) => finishedMs(right) - finishedMs(left));
      const title = first ? first.title?.trim() || AGENT_STRIP_UNTITLED : null;
      notices.push({
        key: `${world.worldKey ?? "local"}:${status}`,
        worldKey: world.worldKey,
        place: world.worldKey === null ? "Local" : worldDisplayName(worlds, world.worldKey),
        label:
          title === null
            ? ROOT_SESSION_STATUS_LABELS[status]
            : `${ROOT_SESSION_STATUS_LABELS[status]}: ${title}${rest.length > 0 ? ` and ${rest.length} more` : ""}`,
        kind: "attention",
        ...(first && title !== null
          ? {
              subject: {
                title,
                environmentId: first.environmentId,
                threadId: first.threadId,
                sessionId: first.sessionId ?? null,
              },
            }
          : {}),
      });
    }
  }
  for (const world of places) {
    const standing =
      world.worldKey === null ? localSelected : !localSelected && activeWorldKey === world.worldKey;
    const finished = world.worldKey === null ? unseen.local : unseen.byWorldKey.has(world.worldKey);
    if (standing || !finished) continue;
    notices.push({
      key: `${world.worldKey ?? "local"}:finished`,
      worldKey: world.worldKey,
      place: world.worldKey === null ? "Local" : worldDisplayName(worlds, world.worldKey),
      label: "Finished while you were away",
      kind: "finished",
    });
  }
  return notices;
}

/** `decisions` alone opens the waiting list; with an id it also opens that one decision. */
export type NotificationDestination =
  | { kind: "decisions"; id?: string }
  | { kind: "contact" | "company"; id: string };

/** Only these view addresses can be opened by the notification URL. */
export function parseNotificationDestination(value: unknown): NotificationDestination | null {
  if (value === "decisions") return { kind: "decisions" };
  if (typeof value !== "string" || value.length > 256) return null;
  const match = /^(contact|company|decisions):([a-zA-Z0-9_-]+)$/.exec(value);
  if (!match) return null;
  return { kind: match[1] as "contact" | "company" | "decisions", id: match[2]! };
}
